import { Injectable, inject } from '@angular/core';
import { Observable, forkJoin, map } from 'rxjs';
import { ApiService } from '../../core/services/api.service';
import { PagedResult } from '../../core/models/api.models';
import { AuditLogDto, PermissionDto, PermissionMatrixRow, RoleDto } from './admin.models';

@Injectable({ providedIn: 'root' })
export class AdminService {
  private readonly api = inject(ApiService);

  listAuditLogs(params?: { page?: number; pageSize?: number }): Observable<PagedResult<AuditLogDto>> {
    return this.api.getPaged<AuditLogDto>('admin/audit-logs', {
      page: params?.page ?? 1,
      pageSize: params?.pageSize ?? 50
    });
  }

  listRoles(): Observable<RoleDto[]> {
    return this.api.get<RoleDto[] | Record<string, unknown>[]>('roles').pipe(
      map(res => (Array.isArray(res) ? res : []).map(r => this.normalizeRole(r as Record<string, unknown>)))
    );
  }

  listPermissions(): Observable<PermissionDto[]> {
    return this.api.get<PermissionDto[] | Record<string, unknown>[]>('roles/permissions').pipe(
      map(res => (Array.isArray(res) ? res : []).map(p => this.normalizePermission(p as Record<string, unknown>)))
    );
  }

  createRole(body: { name: string; description?: string; permissionCodes?: string[] }): Observable<RoleDto> {
    return this.api.post<Record<string, unknown>>('roles', body).pipe(
      map(res => this.normalizeRole(res as Record<string, unknown>))
    );
  }

  setRolePermissions(roleId: string, permissionCodes: string[]): Observable<RoleDto> {
    return this.api.put<Record<string, unknown>>(`roles/${roleId}/permissions`, { permissionCodes }).pipe(
      map(res => this.normalizeRole(res as Record<string, unknown>))
    );
  }

  buildPermissionMatrix(): Observable<{ roles: RoleDto[]; permissions: PermissionDto[]; matrix: PermissionMatrixRow[] }> {
    return forkJoin({
      roles: this.listRoles(),
      permissions: this.listPermissions()
    }).pipe(
      map(({ roles, permissions }) => ({
        roles,
        permissions,
        matrix: this.toMatrix(roles, permissions)
      }))
    );
  }

  toMatrix(roles: RoleDto[], permissions: PermissionDto[]): PermissionMatrixRow[] {
    return permissions.map(permission => ({
      permission,
      roleChecks: Object.fromEntries(
        roles.map(role => [role.id, this.roleHasPermission(role, permission.code)])
      )
    }));
  }

  roleHasPermission(role: RoleDto, permissionCode: string): boolean {
    const codes = role.permissionCodes ?? role.permissions ?? [];
    return codes.some(c => String(c).toLowerCase() === permissionCode.toLowerCase());
  }

  private normalizeRole(row: Record<string, unknown>): RoleDto {
    const rawPermissions = row['permissionCodes'] ?? row['PermissionCodes'] ?? row['permissions'] ?? row['Permissions'];
    const permissionCodes = this.normalizePermissionCodes(rawPermissions);
    const codeValue = row['code'] ?? row['Code'];
    return {
      id: String(row['id'] ?? row['Id'] ?? ''),
      name: String(row['name'] ?? row['Name'] ?? ''),
      code: codeValue == null ? undefined : String(codeValue),
      description: (row['description'] ?? row['Description']) as string | undefined,
      isSystem: !!(row['isSystem'] ?? row['IsSystem']),
      permissionCodes,
      permissions: permissionCodes
    };
  }

  private normalizePermissionCodes(value: unknown): string[] {
    if (!Array.isArray(value)) return [];
    return value.map(item => {
      if (typeof item === 'string') return item;
      if (item && typeof item === 'object') {
        const obj = item as Record<string, unknown>;
        return String(obj['code'] ?? obj['Code'] ?? '');
      }
      return String(item ?? '');
    }).filter(Boolean);
  }

  private normalizePermission(row: Record<string, unknown>): PermissionDto {
    return {
      id: String(row['id'] ?? row['Id'] ?? ''),
      code: String(row['code'] ?? row['Code'] ?? ''),
      name: String(row['name'] ?? row['Name'] ?? ''),
      module: (row['module'] ?? row['Module']) as string | undefined,
      description: (row['description'] ?? row['Description']) as string | undefined
    };
  }
}
